import { apiFetch, fetchWithAuthRetry } from './client'

export type MoneyFig = { amount: number; formatted?: string }

export type ForecastWeek = {
  week_start: string
  close_cents?: number
  close?: MoneyFig | null
  below_zero?: boolean
  below_cushion?: boolean
  in?: MoneyFig | null
  subs_out?: MoneyFig | null
  other_out?: MoneyFig | null
  job_costs_out?: MoneyFig | null
  payroll?: MoneyFig | null
  overhead?: MoneyFig | null
}

export type ForecastLine = {
  amount_cents: number
  source_type: string
  source_id: string | number
  project_id?: number | null
  project_name?: string | null
  pay_app_number?: string | number | null
  effective_date?: string | null
  week_start?: string | null
  direction?: string
  focus?: string
  label?: string
}

export type ConfidenceReason = {
  code: string
  label: string
  fix_action?: string
}

export type AssumptionRow = {
  id: string
  kind: string
  name?: string
  amount_cents?: number | null
  effective_date?: string | null
  project_id?: number | null
  reason?: string | null
  created_at?: string | null
  updated_at?: string | null
  editable?: boolean
}

export type CashActionCard = {
  card_kind: string
  title: string
  fix_action?: string | null
  fact_key: string
}

export type RankedActions = {
  top: CashActionCard[]
  more: CashActionCard[]
}

export type ForecastVersion = {
  id: string
  computed_at?: string
  trigger?: string
  opening_cash_cents?: number | null
  bank_as_of?: string | null
  confidence?: 'high' | 'medium' | 'low' | string
  confidence_reasons?: ConfidenceReason[]
  payload?: {
    weeks?: ForecastWeek[]
    lines?: ForecastLine[]
    safety_cushion_cents?: number
    verdict?: { lowest_week?: string; lowest_line?: string | null; line?: string; status?: string }
    collect_now?: Array<{ project?: string; amount?: MoneyFig; formatted?: string; expected_date?: string }>
    collect_now_total?: MoneyFig | null
    bank_stale?: boolean
    action_count?: number
    projects_table?: ProjectTableRow[]
    collections?: MoneyList<CollectionItem>
    payments_list?: MoneyList<PaymentItem>
    early_pay_offer?: EarlyPayOffer
    monthly_overhead?: MoneyFig | null
    assumptions?: AssumptionRow[]
    actions?: RankedActions
    strip?: {
      expected_collections?: MoneyFig | null
      lowest_projected_balance?: MoneyFig | null
      lowest_week?: string | null
      lowest_line?: string | null
      cash_next_30_days?: MoneyFig | null
    } | null
  } | null
}

export type ProjectTableRow = {
  project_id: number
  project_name: string
  expected_in_cents: number
  expected_out_cents: number
  open_balance_cents: number
  expected_in?: MoneyFig
  expected_out?: MoneyFig
  open_balance?: MoneyFig
  next_risk?: string
  inflow_note?: string | null
}

export type CollectionItem = {
  pay_app_id: number
  pay_app_number?: string | number | null
  project_id?: number | null
  project_name?: string | null
  amount?: MoneyFig
  amount_cents?: number
  status?: string
  display?: string | null
  predicted_paid_at?: string | null
  contract_due?: string | null
  likely_label?: string | null
  likely_from?: string | null
  week_start?: string | null
  focus?: string
}

export type PaymentItem = {
  source_type: string
  source_id?: string | number | null
  project_id?: number | null
  label?: string
  amount?: MoneyFig | null
  amount_cents?: number | null
  unset?: boolean
  focus?: string | null
}

export type MoneyList<T = CollectionItem | PaymentItem> = {
  items?: T[]
  total?: MoneyFig
  total_cents?: number
}

export type EarlyPayOffer = {
  eligible?: boolean
  pay?: MoneyFig
  ladder?: { days: number; rate: number; label: string; save_cents: number }[]
}

export type Envelope<T> = { data: T; error: string | null; message: string }

export type BreakdownGroup = {
  project_id: number | null
  total_cents: number
  by_source_type: { source_type: string; total_cents: number; lines: ForecastLine[] }[]
  lines: ForecastLine[]
}

export type Breakdown = {
  version_id: string
  week: string | null
  total_cents: number
  parent_cents: number
  groups: BreakdownGroup[]
  lines: ForecastLine[]
}

export type ProjectDrawer = Breakdown & {
  project_id?: number
  project_name?: string | null
  contract?: { original?: MoneyFig | null; revised?: MoneyFig | null } | null
  open_pay_apps?: Array<{
    pay_app_id: number
    status?: string
    display?: string | null
    predicted_paid_at?: string | null
    amount_cents?: number
    bucket_date?: string | null
  }>
  subcontract_amounts?: Array<{
    vendor_name?: string
    amount_cents?: number
    not_a_cash_line?: boolean
  }>
  vendor_bills?: ForecastLine[]
  retainage?: ForecastLine[]
  assumptions?: ForecastLine[]
  per_project_margin?: {
    projected_margin?: MoneyFig | null
    completeness?: string | null
  } | null
  next_risk?: string | null
}

export type ForecastEffect = {
  this_week: { before_cents: number | null; after_cents: number | null }
  lowest: { before_cents: number | null; after_cents: number | null }
}

export type AssumptionWrite = {
  kind?: string
  amount_cents?: number | null
  effective_date?: string
  project_id?: number | null
  reason?: string | null
  target_source_type?: string | null
  target_source_id?: number | null
}

export function fetchCashForecast() {
  return apiFetch<Envelope<{
    version: ForecastVersion | null
    actions?: RankedActions
    audit_count?: number
    pending?: boolean
  }>>(
    'GET',
    '/api/cash-report/forecast',
  )
}

export function fetchCashBreakdown(
  versionId: string,
  weekOrOpts: string | { week?: string; projectId?: number; scope?: string } = {},
) {
  const opts = typeof weekOrOpts === 'string' ? { week: weekOrOpts } : weekOrOpts
  const q = new URLSearchParams()
  if (opts.week) q.set('week', opts.week)
  if (opts.projectId != null) q.set('project_id', String(opts.projectId))
  if (opts.scope) q.set('scope', opts.scope)
  return apiFetch<Envelope<Breakdown & ProjectDrawer>>(
    'GET',
    `/api/cash-report/forecast/${encodeURIComponent(versionId)}/breakdown?${q}`,
  )
}

export function saveFridayCheckIn(body: {
  payroll_cents?: number | null
  bills_cents?: number | null
  unusual_cents?: number | null
  cash_on_hand_cents?: number | null
  effective_date?: string
}) {
  return apiFetch<Envelope<{ version: ForecastVersion; actions?: unknown }>>(
    'POST',
    '/api/cash-report/friday-checkin',
    body,
  )
}

export function createAssumption(body: AssumptionWrite) {
  return apiFetch<Envelope<{ assumption: AssumptionRow; version: ForecastVersion }>>(
    'POST',
    '/api/cash-report/assumptions',
    body,
  )
}

export function editAssumption(id: string, body: AssumptionWrite) {
  return apiFetch<Envelope<{ assumption: AssumptionRow; version: ForecastVersion }>>(
    'PATCH',
    `/api/cash-report/assumptions/${encodeURIComponent(id)}`,
    body,
  )
}

export function deleteAssumption(id: string) {
  return apiFetch<Envelope<{ assumption: AssumptionRow; version: ForecastVersion }>>(
    'DELETE',
    `/api/cash-report/assumptions/${encodeURIComponent(id)}`,
  )
}

export function previewDeleteAssumption(id: string) {
  return apiFetch<Envelope<ForecastEffect>>(
    'POST',
    `/api/cash-report/assumptions/${encodeURIComponent(id)}/preview-delete`,
  )
}

export function listAssumptions() {
  return apiFetch<Envelope<{ items: AssumptionRow[]; audit_count: number }>>(
    'GET',
    '/api/cash-report/assumptions',
  )
}

export function recordCashAction(body: {
  action: 'done' | 'remind' | 'hide'
  card_kind: string
  fact_key: string
  remind_at?: string
}) {
  return apiFetch<Envelope<{ dismissal: { id: string } }>>(
    'POST',
    '/api/cash-report/actions',
    body,
  )
}

export async function downloadCashReportPdf(versionId?: string): Promise<Blob> {
  const q = versionId ? `?version_id=${encodeURIComponent(versionId)}` : ''
  const res = await fetchWithAuthRetry(`/api/cash-report/pdf${q}`)
  if (!res.ok) throw new Error('PDF failed')
  return res.blob()
}

export function createCashReportShare(versionId?: string) {
  return apiFetch<Envelope<{
    token: string
    url: string
    pdf_url: string
    expires_at: string
    version_id: string
  }>>('POST', '/api/cash-report/share', { version_id: versionId || null })
}

export function revokeCashReportShare(token: string) {
  return apiFetch<Envelope<{ revoked: boolean }>>('POST', '/api/cash-report/share/revoke', { token })
}
